
import { useState } from "react";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { AggregatedData, ProductTranslation } from "./types";

export const useAggregatedData = () => {
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  const fetchAggregatedData = async (collectionId: string) => {
    setIsLoading(true);
    
    try {
      // Fetch approved product descriptions
      const { data: products, error: productsError } = await supabase
        .from("product_descriptions")
        .select("id, original_text, generated_description, improved_description, article_number, product_details")
        .eq("collection_id", collectionId)
        .eq("status", "approved");

      if (productsError) throw productsError;

      if (!products || products.length === 0) {
        toast({
          title: "No data",
          description: "No approved products found for this collection",
          variant: "destructive",
        });
        return null;
      }

      // Fetch approved translations with translated_details
      const { data: translations, error: translationsError } = await supabase
        .from("translations")
        .select(`
          id,
          language_code,
          translated_descriptions!inner (
            product_description_id,
            translated_text,
            improved_translation,
            translated_details,
            status
          )
        `)
        .eq("collection_id", collectionId)
        .eq("translated_descriptions.status", "approved");

      if (translationsError) throw translationsError;

      // Create translation map
      const translationMap = new Map<string, ProductTranslation>();
      const languages = new Set<string>();

      translations?.forEach(translation => {
        languages.add(translation.language_code);
        
        translation.translated_descriptions?.forEach(td => {
          if (!translationMap.has(td.product_description_id)) {
            translationMap.set(td.product_description_id, {});
          }
          
          const translatedText = td.improved_translation || td.translated_text || '';
          const translatedDetails = td.translated_details || '';
          
          translationMap.get(td.product_description_id)![translation.language_code] = {
            text: translatedText,
            details: translatedDetails
          };
        });
      });

      if (languages.size === 0) {
        toast({
          title: "No translations",
          description: "No approved translations found for this collection",
          variant: "destructive",
        });
        return null;
      }

      // Build aggregated data
      const aggregatedData: AggregatedData[] = products.map(product => {
        const baseData: AggregatedData = {
          originalText: product.original_text || '',
          generatedDescription: product.improved_description || product.generated_description || '',
          articleNumber: product.article_number || '',
          productDetails: product.product_details || ''
        };

        // Add translation columns for each language (both text and details)
        const productTranslations = translationMap.get(product.id) || {};
        languages.forEach(lang => {
          const translation = productTranslations[lang] || { text: '', details: '' };
          baseData[`translation_${lang}`] = translation.text;
          baseData[`translation_details_${lang}`] = translation.details;
        });

        return baseData;
      });

      return { aggregatedData, languages: Array.from(languages) };
    } catch (error) {
      console.error('Aggregated data fetch error:', error);
      toast({
        title: "Error",
        description: "Failed to fetch aggregated data",
        variant: "destructive",
      });
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    fetchAggregatedData,
    isLoading
  };
};
